// Removed unused imports
import { Card, CardContent, CardHeader, CardTitle, TransparentCard } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ExternalLink, Shield, AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import PageWithNavbar from "@/components/PageWithNavbar";

interface ExchangeGuide {
  id: string;
  name: string;
  steps: {
    title: string;
    description: string;
    warning?: string;
  }[];
  apiUrl: string;
  notes?: string[];
}

const exchangeGuides: ExchangeGuide[] = [
  {
    id: "coinbase",
    name: "Coinbase Pro",
    apiUrl: "https://pro.coinbase.com/profile/api",
    steps: [
      {
        title: "Log into Coinbase Pro",
        description: "Visit pro.coinbase.com and sign into your account"
      },
      {
        title: "Navigate to API Settings",
        description: "Click on your profile icon → API → Create API Key"
      },
      {
        title: "Configure Permissions",
        description: "Select 'View' and 'Trade' permissions. Avoid 'Transfer' unless needed.",
        warning: "Only enable Transfer permissions if you plan to move funds between accounts"
      },
      {
        title: "Add IP Whitelist",
        description: "Add your IP address for security. Leave blank if using dynamic IP."
      },
      {
        title: "Save Credentials",
        description: "Copy your API Key, Secret, and Passphrase. Store them securely in Doyen."
      }
    ],
    notes: [
      "Coinbase Pro offers sandbox environment for testing",
      "Rate limits: 10 requests per second for private endpoints",
      "API documentation: docs.pro.coinbase.com"
    ]
  },  {
    id: "kraken",
    name: "Kraken",
    apiUrl: "https://www.kraken.com/u/security/api",
    steps: [
      {
        title: "Access Security Settings",
        description: "Log into Kraken → Settings → API"
      },
      {
        title: "Generate New Key",
        description: "Click 'Generate New Key' and provide a key description"
      },
      {
        title: "Set Key Permissions",
        description: "Enable 'Query Funds', 'Query Open/Closed Orders', and 'Create & Modify Orders'",
        warning: "Be cautious with 'Withdraw Funds' permission"
      },
      {
        title: "Configure Security",
        description: "Set up 2FA requirement and IP restrictions if desired"
      },
      {
        title: "Save API Credentials",
        description: "Copy your API Key and Private Key. The private key is only shown once."
      }
    ],
    notes: [
      "Kraken requires 2FA for API key generation",
      "Rate limits: 1 call per second for most endpoints",
      "Supports both REST and WebSocket APIs"
    ]
  },  {
    id: "gemini",
    name: "Gemini",
    apiUrl: "https://exchange.gemini.com/settings/api",
    steps: [
      {
        title: "Go to API Settings",
        description: "Log into Gemini → Settings → API Keys"
      },
      {
        title: "Create Master API Key",
        description: "Click 'Create a new API key' and select 'Master' scope"
      },
      {
        title: "Configure Permissions",
        description: "Enable 'Trading' permissions. 'Fund Management' is optional.",
        warning: "Fund Management allows deposits/withdrawals"
      },
      {
        title: "Set Session Length",
        description: "Choose session length (up to 30 days) or make it require heartbeat"
      },
      {
        title: "Download Credentials",
        description: "Save your API Key and API Secret securely"
      }
    ],
    notes: [
      "Gemini offers both sandbox and production environments",
      "Rate limits: 600 requests per minute",
      "Strong security features with session management"
    ]
  },  {
    id: "binance",
    name: "Binance.US",
    apiUrl: "https://www.binance.us/en/usercenter/settings/api-management",
    steps: [
      {
        title: "Access API Management",
        description: "Log into Binance.US → Profile → API Management"
      },
      {
        title: "Create API Key",
        description: "Click 'Create API' and complete 2FA verification"
      },
      {
        title: "Configure API Restrictions",
        description: "Enable 'Enable Reading' and 'Enable Spot & Margin Trading'",
        warning: "Avoid 'Enable Withdrawals' unless absolutely necessary"
      },
      {
        title: "Set IP Restrictions",
        description: "Add trusted IP addresses for enhanced security"
      },
      {
        title: "Save API Information",
        description: "Copy your API Key and Secret Key immediately"
      }
    ],
    notes: [
      "Requires completion of advanced verification",
      "Rate limits: 1200 requests per minute",
      "IP whitelist strongly recommended for security"
    ]
  },  {
    id: "crypto-com",
    name: "Crypto.com",
    apiUrl: "https://crypto.com/exchange/document/description",
    steps: [
      {
        title: "Access API Settings",
        description: "Log into Crypto.com Exchange → Profile → API Keys"
      },
      {
        title: "Create New API Key",
        description: "Click 'Create API Key' and complete 2FA authentication"
      },
      {
        title: "Select API Scopes",
        description: "Choose 'spot:read', 'spot:trade', and 'user:read' scopes",
        warning: "Only select 'user:write' if you need withdrawal capabilities"
      },
      {
        title: "Configure IP Whitelist",
        description: "Add your IP addresses to the whitelist for security"
      },
      {
        title: "Generate and Save",
        description: "Complete the process and securely store your API credentials"
      }
    ],
    notes: [
      "Requires institutional verification for full API access",
      "Rate limits: Varies by endpoint (typically 100 req/min)",
      "WebSocket feeds available for real-time data"
    ]
  }
];

export const SetupPage = () => {
  const navigate = useNavigate();

  // Handle URL hash navigation on component mount
  useEffect(() => {
    const handleHashNavigation = () => {
      const hash = window.location.hash.slice(1); // Remove the # symbol
      if (hash) {
        // Small delay to ensure the DOM is fully rendered
        setTimeout(() => {
          const element = document.getElementById(hash);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    };

    // Handle hash on initial load
    handleHashNavigation();

    // Handle hash changes (if user navigates with hash)
    window.addEventListener('hashchange', handleHashNavigation);

    return () => {
      window.removeEventListener('hashchange', handleHashNavigation);
    };
  }, []);

  return (
    <PageWithNavbar>
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-3xl md:text-4xl font-bold">
            Exchange{" "}
            <span className="bg-gradient-to-b from-primary/60 to-primary text-transparent bg-clip-text">
              Setup
            </span>          
          </h1>          
          <p className="text-xl text-muted-foreground mt-2 mb-6">
            Step-by-step setup guides for Coinbase, Kraken, Gemini, Binance.US, and Crypto.com API integration.
          </p>

          {/* General Security Guidelines */}
          <Card className="mb-8 bg-muted/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                General Security Best Practices
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold mb-3">API Key Security</h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">✓</span>
                      Use minimal permissions (read + trade only)
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">✓</span>
                      Enable IP whitelisting when possible
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">✓</span>
                      Store API keys securely in Doyen
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-1">✗</span>
                      Never enable withdrawal permissions unless needed
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold mb-3">Account Protection</h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">✓</span>
                      Enable 2FA on all exchange accounts
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">✓</span>
                      Use strong, unique passwords
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">✓</span>
                      Regularly review API key usage
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">✓</span>
                      Monitor account activity regularly
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="mb-8 border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-900/20">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <Shield className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-yellow-800 dark:text-yellow-200 mb-1">Security Notice</h4>
                  <p className="text-sm text-yellow-700 dark:text-yellow-300">
                    Never share your API keys with anyone. Doyen stores your credentials locally and encrypted. 
                    Always use IP restrictions and minimal permissions when creating API keys.
                  </p>
                </div>
              </div>            </CardContent>
          </Card>

          {/* Quick Navigation */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-4">Jump to Exchange:</h3>
            <div className="flex flex-wrap gap-3">
              {exchangeGuides.map((exchange) => (
                <Button
                  key={exchange.id}
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const element = document.getElementById(exchange.id);
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                  className="flex items-center gap-2"
                >
                  {exchange.name}
                </Button>
              ))}
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-4">Example with Kraken</h3>
          <div className="w-full bg-muted/10 rounded-lg p-4 text-center">
              <video 
              controls
              autoPlay 
              loop 
              muted 
              playsInline
              className="w-full mx-auto"
              >
                  <source src="/Exchanges-Connect-Kraken.webm" type="video/webm" />
                  Your browser does not support the video tag.
              </video>
          </div>
          <p className="text-sm text-muted-foreground">
            An example of connecting a Kraken account to Doyen using API keys. The process is similar for other exchanges.
          </p>
        </div>

        <div className="grid gap-8 mt-6">
          {exchangeGuides.map((exchange, index) => (
            <Card key={index} id={exchange.id} className="border-2 scroll-mt-24">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <p className="font-bold text-3xl">{exchange.name}</p>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => window.open(exchange.apiUrl, '_blank')}
                  >
                    <ExternalLink className="mr-2 h-4 w-4" />
                    API Settings
                  </Button>
                </div>
              </CardHeader>

              <CardContent>
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      API Key Setup
                    </h4>
                    <div className="space-y-4">
                      {exchange.steps.map((step, stepIndex) => (
                        <div key={stepIndex} className="flex gap-4">
                          <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-sm font-semibold text-primary">
                            {stepIndex + 1}
                          </div>
                          <div className="flex-1">
                            <h5 className="font-medium">{step.title}</h5>
                            <p className="text-sm text-muted-foreground mt-1">{step.description}</p>
                            {step.warning && (
                              <div className="mt-2 flex items-start gap-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded-md">
                                <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                                <p className="text-sm text-yellow-800 dark:text-yellow-200">{step.warning}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {exchange.notes && (
                    <div>
                      <h4 className="font-semibold mb-3">Important Notes</h4>
                      <ul className="space-y-2">
                        {exchange.notes.map((note, noteIndex) => (
                          <li key={noteIndex} className="text-sm text-muted-foreground flex items-start gap-2">
                            <span className="text-primary mt-1">•</span>
                            {note}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}        </div>

        {/* Call to Action */}
        <section className="text-center mt-12">
          <TransparentCard className="bg-primary/5 p-8">
            <h3 className="text-2xl font-bold mb-4">
              Ready to Connect Your Exchanges?
            </h3>
            <p className="text-muted-foreground mb-6">
              Follow these setup instructions to safely connect your exchange accounts and start trading with Doyen's 
              advanced tools and unified interface.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate("/manual")}
                className="bg-primary hover:bg-primary/90"
              >
                Get Started with Doyen
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate("/roadmap")}
              >
                View Roadmap
              </Button>
            </div>
          </TransparentCard>
        </section>
      </div>
    </div>
    </PageWithNavbar>
  );
};

export default SetupPage;
